'use client';
import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

export type TooltipPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  placement?: TooltipPlacement;
  className?: string;
  forceOpen?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  placement = 'top',
  className = '',
  forceOpen = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const isOpen = forceOpen || visible;
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    ax: number;
    ay: number;
  } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const tooltipId = React.useId();

  const updatePosition = React.useCallback(() => {
    if (!isOpen || !triggerRef.current || !tooltipRef.current) {
      setCoords(null);
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 8;

    let top = 0;
    let left = 0;
    let ax = tooltipRect.width / 2 - 5;
    let ay = -5;
    const anchorX = triggerRect.left + triggerRect.width / 2;
    const anchorY = triggerRect.top + triggerRect.height / 2;

    switch (placement) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - gap;
        left = anchorX - tooltipRect.width / 2;
        ax = anchorX - left - 5;
        ay = tooltipRect.height - 5;
        break;
      case 'bottom':
        top = triggerRect.bottom + gap;
        left = anchorX - tooltipRect.width / 2;
        ax = anchorX - left - 5;
        ay = -5;
        break;
      case 'left':
        top = anchorY - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - gap;
        ax = tooltipRect.width - 5;
        ay = anchorY - top - 5;
        break;
      case 'right':
        top = anchorY - tooltipRect.height / 2;
        left = triggerRect.right + gap;
        ax = -5;
        ay = anchorY - top - 5;
        break;
      case 'top-start':
        top = triggerRect.top - tooltipRect.height - gap;
        left = triggerRect.left;
        ay = tooltipRect.height - 5;
        ax = Math.max(10, Math.min(anchorX - left - 5, tooltipRect.width - 20));
        break;
      case 'top-end':
        top = triggerRect.top - tooltipRect.height - gap;
        left = triggerRect.right - tooltipRect.width;
        ay = tooltipRect.height - 5;
        ax = Math.max(10, Math.min(anchorX - left - 5, tooltipRect.width - 20));
        break;
      case 'bottom-start':
        top = triggerRect.bottom + gap;
        left = triggerRect.left;
        ax = Math.max(10, Math.min(anchorX - left - 5, tooltipRect.width - 20));
        ay = -5;
        break;
      case 'bottom-end':
        top = triggerRect.bottom + gap;
        left = triggerRect.right - tooltipRect.width;
        ax = Math.max(10, Math.min(anchorX - left - 5, tooltipRect.width - 20));
        ay = -5;
        break;
    }

    setCoords({ top, left, ax, ay });
  }, [placement, isOpen]);

  React.useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleUpdate = () => updatePosition();
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [updatePosition, isOpen]);

  const canUseDom = typeof document !== 'undefined';
  const tooltipNode =
    isOpen && mounted && canUseDom
      ? ReactDOM.createPortal(
          <span
            ref={tooltipRef}
            role='tooltip'
            id={tooltipId}
            className={`tooltip tooltip-${placement} is-show`}
            style={
              {
                top: coords?.top ?? 0,
                left: coords?.left ?? 0,
                zIndex: 1300,
                pointerEvents: 'none',
                visibility: coords ? 'visible' : 'hidden',
                '--ax': `${coords?.ax ?? 10}px`,
                '--ay': `${coords?.ay ?? -5}px`,
              } as React.CSSProperties
            }
          >
            {content}
          </span>,
          document.body
        )
      : null;

  return (
    <span
      className={`tooltip-trigger ${className}`}
      ref={triggerRef}
      tabIndex={0}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      aria-describedby={isOpen ? tooltipId : undefined}
    >
      {children}
      {tooltipNode}
    </span>
  );
};
